import React, { useState } from "react";
import "./Register.scss";
import authService from "../../services/auth-service";
import { updateDBHeaders } from "../../db/direct-couchdb";
import LocationButton from "../LocationButton/LocationButton";

function Register(props) {
  // Personal Information
  const [email, setEmail] = useState("");
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [mobile, setMobile] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [pass, setPass] = useState("");
  
  // Address Information
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  
  // Form State
  const [isAdmin, setAdmin] = useState("0");
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [registering, setRegistering] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const [locationSuccess, setLocationSuccess] = useState(false);
  const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
  
  // Allowed pincodes for service area
  const allowedPincodes = ['500046', '500019', '500032', '500084'];

  const validatePincode = (pincode) => {
    if (!pincode || pincode.trim() === '') return false;
    return allowedPincodes.includes(pincode.trim());
  };

  // Reset to customer role on email change (only relevant in development)
  React.useEffect(() => {
    setAdmin("0");
  }, [email]);

  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return false;
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ''));
  };

  const checkPhoneExists = async (phone) => {
    try {
      const response = await authService.checkPhoneExists(phone);
      return response;
    } catch (error) {
      console.error('Error checking phone:', error);
      return false;
    }
  };

  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background-color: #4CAF50;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 16px;
      font-weight: 500;
      animation: slideIn 0.3s ease-out;
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  };

  const handleUserRegistration = async () => {
    if (await validateInputs()) {
      setRegistering(true);
      try {
        const fullName = `${fname} ${lname}`.trim();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        const address = {
          addressLine1,
          addressLine2,
          city,
          state,
          pincode
        };
        const result = await authService.register(
          email,
          pass,
          fullName,
          mobile,
          isAdmin === "1",
          "", // No OTP required
          timezone,
          address
        );
        
        updateDBHeaders();
        localStorage.setItem('userTimezone', timezone);
        sessionStorage.setItem("isUserAuthenticated", "true");
        sessionStorage.setItem("customerName", fullName);
        sessionStorage.setItem("isAdmin", String(result.user.role === 'admin'));
        sessionStorage.setItem("customerId", result.user.id);
        sessionStorage.setItem("customerEmail", result.user.email);
        sessionStorage.setItem("customerPhone", mobile);
        sessionStorage.removeItem("trialJustClaimed");
        sessionStorage.removeItem("hasSubscriptions");
        
        showToast('Registration successful! Redirecting...');
        
        setTimeout(() => {
          if (result.user.role === 'admin') {
            window.location.href = '/admin';
          } else {
            window.location.href = '/customer';
          }
        }, 1500);
      } catch (err) {
        console.log("Registration failed", err);
        setError(err.message || "Registration failed");
      } finally {
        setRegistering(false);
      }
    }
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validateInputs = async () => {
    if (!validateEmail(email)) {
      setError("Please provide a valid email address.");
      return false;
    } else if (fname.trim() === "") {
      setError("Please provide your first name.");
      return false;
    } else if (mobile.trim() === "") {
      setError("Please provide your mobile number.");
      return false;
    } else if (!validatePhone(mobile)) {
      setError("Please provide a valid 10-digit mobile number.");
      return false;
    }
    
    // Check if phone number already exists
    const phoneExists = await checkPhoneExists(mobile);
    if (phoneExists) {
      setError("This mobile number is already registered. Please use a different number.");
      return false;
    }
    
    if (addressLine1.trim() === "") {
      setError("Please provide your address.");
      return false;
    } else if (city.trim() === "") {
      setError("Please provide your city.");
      return false;
    } else if (state.trim() === "") {
      setError("Please provide your state.");
      return false;
    } else if (pincode.trim() === "") {
      setError("Please provide your pincode.");
      return false;
    } else if (!validatePincode(pincode)) {
      setError(`Sorry, we currently deliver only to these pincodes: ${allowedPincodes.join(', ')}. Please check if your pincode is in our service area.`);
      return false;
    } else if (!validatePassword(pass)) {
      setError("Password must be at least 6 characters long.");
      return false;
    } else if (!termsAccepted) {
      setError("Please accept the terms and conditions to proceed.");
      return false;
    }
    setError("");
    return true;
  };

  const nextStep = async () => {
    if (currentStep === 1) {
      if (!validateEmail(email) || fname.trim() === "" || mobile.trim() === "") {
        setError("Please fill all required personal information fields.");
        return;
      }
      if (!validatePhone(mobile)) {
        setError("Please provide a valid 10-digit mobile number.");
        return;
      }
      
      // Check phone uniqueness before proceeding
      const phoneExists = await checkPhoneExists(mobile);
      if (phoneExists) {
        setError("This mobile number is already registered. Please use a different number.");
        return;
      }
    }
    setError("");
    setCurrentStep(currentStep + 1);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setError("");
    setLocationMessage('');
    setLocationSuccess(false);
  };

  const handleLocationDetected = (address, message) => {
    // Auto-fill address fields
    setAddressLine1(address.addressLine1 || '');
    setAddressLine2(address.addressLine2 || '');
    setCity(address.city || '');
    setState(address.state || '');
    
    // Check if detected pincode is in allowed list
    const detectedPincode = address.pincode || '';
    console.log('Detected pincode:', detectedPincode); // Debug log
    
    if (detectedPincode && validatePincode(detectedPincode)) {
      setPincode(detectedPincode);
      setLocationMessage(message || 'Location detected successfully!');
    } else {
      setPincode('');
      const pincodeText = detectedPincode ? detectedPincode : 'unknown';
      setLocationMessage(`Location detected, but we don't deliver to pincode ${pincodeText}. We deliver to: ${allowedPincodes.join(', ')}`);
    }
    
    setLocationSuccess(true);
    setError('');
    
    // Clear success message after 7 seconds (longer for pincode message)
    setTimeout(() => {
      setLocationMessage('');
      setLocationSuccess(false);
    }, 7000);
  };

  const handleLocationError = (errorMessage) => {
    setLocationMessage('');
    setLocationSuccess(false);
    // Don't set as main error, just show in location button
  };

  return (
    <div className="register-container">
      <div className="register-header">
        <h1>Create Your Account</h1>
        <div className="step-indicator">
          <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
          <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Step 1: Personal Information */}
      {currentStep === 1 && (
        <div className="form-step">
          <h2>Personal Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={fname}
                onChange={(e) => setFname(e.target.value)}
                placeholder="Enter your first name"
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={lname}
                onChange={(e) => setLname(e.target.value)}
                placeholder="Enter your last name (Optional)"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Mobile Number *</label>
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="Enter your 10-digit mobile number"
              maxLength="10"
            />
            <small style={{color: '#666'}}>Primary login credential - Enter 10-digit mobile number</small>
          </div>
          
          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
            />
            <small style={{color: '#666'}}>Required for notifications and account recovery</small>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
              />
              <small style={{color: '#999'}}>Optional</small>
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select Gender (Optional)</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={nextStep} 
              className="btn-primary"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Address, Password, and Terms */}
      {currentStep === 2 && (
        <div className="form-step">
          <h2>Complete Your Registration</h2>
          
          {/* Address Section */}
          <h3>Address Information</h3>
          
          {/* Location Button */}
          <LocationButton 
            onLocationDetected={handleLocationDetected}
            onError={handleLocationError}
            className="form-integration"
          />
          
          {/* Location Success Message */}
          {locationSuccess && locationMessage && (
            <div className="location-success-message">
              <small>{locationMessage}</small>
            </div>
          )}
          <div className="form-group">
            <label>Address Line 1 *</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="House/Flat No, Building Name"
            />
          </div>
          
          <div className="form-group">
            <label>Address Line 2</label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Street, Area, Landmark (Optional)"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter your city"
              />
            </div>
            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="Enter your state"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Pincode *</label>
            <input
              type="text"
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter your pincode"
              maxLength="6"
            />
            <small style={{color: '#666'}}>We deliver to: {allowedPincodes.join(', ')}</small>
          </div>
          
          {/* Password Section */}
          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="Enter a secure password (min 6 characters)"
            />
            <small>Password must be at least 6 characters long</small>
          </div>
          
          {/* Account Type - only visible in development/localhost */}
          {isDevelopment && (
            <div className="form-group">
              <label>Account Type</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    value="0"
                    checked={isAdmin === "0"}
                    onChange={() => setAdmin("0")}
                  />
                  <span>Customer</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    value="1"
                    checked={isAdmin === "1"}
                    onChange={() => setAdmin("1")}
                  />
                  <span>Admin</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Terms and Conditions */}
          <h3>Terms and Conditions</h3>
          <div className="terms-section">
            <div className="terms-content">
              <h4>About Our Company</h4>
              <p>Organic Greens Hub is committed to providing fresh, healthy, and nutritious products to support your wellness journey.</p>
              
              <h4>About Our Ingredients</h4>
              <p><strong>Leafy Greens:</strong> All our leafy greens are 100% pure organic, grown without pesticides or chemicals.</p>
              <p><strong>Other Ingredients:</strong> Apart from leafy greens, other ingredients may or may not be organic, but are carefully selected for quality and freshness.</p>
              
              <h4>Order Terms</h4>
              <ul>
                <li>Orders are processed and delivered as per selected time slots</li>
                <li>Delivery schedule: Monday to Friday</li>
                <li>Products are subject to availability</li>
              </ul>
              
              <h4>Payment and Refund Policy</h4>
              <ul>
                <li><strong>Same Day Cancellation:</strong> Full refund available if order is cancelled on the same day of ordering</li>
                <li><strong>Refund Processing:</strong> Refunds may take 5 business days to process</li>
                <li>In case of delivery issues, we will provide replacement products or refund</li>
              </ul>
              
              <h4>Subscription Terms</h4>
              <ul>
                <li>Subscription is billed monthly in advance</li>
                <li>Delivery schedule: Monday to Friday (20 working days per month)</li>
                <li>Products are automatically selected to ensure variety</li>
                <li>All subscription payments made are non-refundable</li>
              </ul>
            </div>
            
            <div className="terms-checkbox">
              <label className="checkbox-container">
                <input 
                  type="checkbox" 
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span className="checkmark"></span>
                I agree to the terms and conditions, understand that all leafy greens are organic while other ingredients may vary, and accept the refund and subscription policies.
              </label>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" onClick={prevStep} className="btn-secondary">
              Back
            </button>
            <button 
              type="button" 
              onClick={handleUserRegistration}
              className="btn-primary"
              disabled={registering || !termsAccepted || !validatePassword(pass) || addressLine1.trim() === "" || city.trim() === "" || state.trim() === "" || pincode.trim() === ""}
            >
              {registering ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </div>
      )}

      <div className="login-link" onClick={() => props.navigateToLoginPage()}>
        Already have an account? Sign In
      </div>
    </div>
  );
}

export default Register;
